import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { loadAnnotations, setAnnotationsVisible, createNote, setActiveAnnotationEntity } from '../actions/annotations.actions';
import {
  makeGetAnnotationsForEntityTimeRange,
  getVisible,
  getCreateMode,
  getActiveEntity,
  getLoadingForEntity,
  shouldFetchAnnotations
} from '../selectors/annotations.selectors';
import { entityKeyFor } from '../reducers/annotations.reducer';
import { SUPPORTED_ANNOTATABLE_TYPES, isSupportedAnnotatableType } from '../utils/annotationTypes';
import AnnotationOverlay from './AnnotationOverlay';
import RangeLaneBand from './RangeLaneBand';
import NoteCreationModal from './NoteCreationModal';
import TrendChart from '../../Machines/MachineDetails/MachineCharts/components/TrendChart';
import {
  AnnotatedChartContainer,
  ChartWrapper,
} from './AnnotatedChart.styled';

function AnnotatedChart(props) {
  const {
    children,
    annotations,
    timeRange,
    annotationsVisible,
    createMode,
    annotationsLoading,
    chartHeight,
    chartWidth,
    showAnnotationControls = true,
    className,
    overlayMarkers,
    overlayDottedLines,
    onAnnotationClick,
    onAnnotationCreate,
    entityType,
    entityId,
    annotationTypes,
    activeEntity,
    shouldFetch,
    loadAnnotations,
    createNote,
    setActiveAnnotationEntity
  } = props;

  // This chart's key in the entity-partitioned annotations slice.
  const thisEntityKey = useMemo(() => entityKeyFor(entityType, entityId), [entityType, entityId]);

  const [mounted, setMounted] = useState(false);
  const [pendingNote, setPendingNote] = useState(null);
  const [savingNote, setSavingNote] = useState(false);
  // The plot's currently-visible x-domain ({ start, end } ms), reported by the
  // trend chart so the range-lane band above it stays aligned through zoom/pan.
  const [visibleDomain, setVisibleDomain] = useState(null);
  // Range currently hovered in the band above the chart; relayed to the plot so
  // it can spotlight that span (full-height tint), mirroring in-plot hover.
  const [highlightAnnotation, setHighlightAnnotation] = useState(null);

  const handleVisibleDomainChange = useCallback((d) => {
    if (!d || !Number.isFinite(d.start) || !Number.isFinite(d.end)) return;
    setVisibleDomain(prev => (prev && prev.start === d.start && prev.end === d.end ? prev : d));
  }, []);

  const handleAnnotationClick = useCallback((annotation) => {
    if (onAnnotationClick) onAnnotationClick(annotation);
  }, [onAnnotationClick]);

  // When a parent supplies its own create handler, defer to it; otherwise
  // AnnotatedChart owns the note-creation flow (modal + dispatch).
  const handleAnnotationCreate = useCallback((payload) => {
    if (onAnnotationCreate) {
      onAnnotationCreate(payload);
      return;
    }
    if (!payload || !payload.startTime) return;
    setPendingNote({ startTime: payload.startTime, endTime: payload.endTime || null });
  }, [onAnnotationCreate]);

  const handleCloseNoteModal = useCallback(() => {
    setPendingNote(null);
  }, []);

  const handleSaveNote = useCallback((noteText) => {
    if (!pendingNote) return;

    const noteParams = {
      tag_id: entityId,
      note_text: noteText,
      timestamp: pendingNote.startTime,
      end_time: pendingNote.endTime || null
    };

    const reloadParams = {
      annotatable_type: entityType,
      annotatable_id: entityId,
      types: annotationTypes
    };
    if (timeRange && timeRange.start && timeRange.end) {
      reloadParams.start_time = new Date(timeRange.start).toISOString();
      reloadParams.end_time = new Date(timeRange.end).toISOString();
    }

    setSavingNote(true);
    Promise.resolve(createNote(noteParams, reloadParams)).then(
      () => { setSavingNote(false); setPendingNote(null); },
      () => { setSavingNote(false); }
    );
  }, [pendingNote, entityId, entityType, annotationTypes, timeRange, createNote]);

  // Mark mounted once
  useEffect(() => { setMounted(true); }, []);

  // Stable primitives for effect deps
  const startTs = useMemo(() => (timeRange && timeRange.start ? new Date(timeRange.start).getTime() : null), [timeRange]);
  const endTs = useMemo(() => (timeRange && timeRange.end ? new Date(timeRange.end).getTime() : null), [timeRange]);
  const typesKey = useMemo(() => JSON.stringify(annotationTypes || []), [annotationTypes]);

  useEffect(() => {
    if (!annotationsVisible) return;
    if (!entityType || !entityId || !startTs || !endTs) return;
    // The API only serves scopes in SUPPORTED_ANNOTATABLE_TYPES. A machine- or
    // component-scoped chart (e.g. the health timeline widget) would otherwise
    // fire a request the backend rejects with 400 "annotatable_type: Invalid
    // value", surfacing an error toast on every visit while annotations are on.
    if (!isSupportedAnnotatableType(entityType)) {
      if (process.env.NODE_ENV !== 'production') {
        // Say so out loud: silently skipping the fetch is right for users, but
        // leaves a developer wiring up a new scope with no annotations, no
        // error, and nothing to search for.
        // eslint-disable-next-line no-console
        console.warn(
          `[Annotations] annotatable_type "${entityType}" is not served by the API `
          + `(supported: ${SUPPORTED_ANNOTATABLE_TYPES.join(', ')}). Annotations are `
          + 'disabled for this chart. Widen SUPPORTED_ANNOTATABLE_TYPES and the '
          + 'backend GetAnnotationsSchema together to enable it.'
        );
      }
      return;
    }
    if (!Number.isFinite(startTs) || !Number.isFinite(endTs)) return;

    const params = {
      annotatable_type: entityType,
      annotatable_id: entityId,
      start_time: new Date(startTs).toISOString(),
      end_time: new Date(endTs).toISOString(),
      types: annotationTypes
    };
    if (shouldFetch(params)) {
      loadAnnotations(params);
    }
  }, [annotationsVisible, entityType, entityId, startTs, endTs, typesKey, shouldFetch, loadAnnotations, annotationTypes]);

  // Match by component identity, not a `type` prop: `type="trend"` is forwarded
  // through every wrapper level (TagChart -> TagChartView -> Chart -> TrendChart),
  // so a string check would also match outer wrappers and mis-plumb them
  // (duplicate band, disabled markers). True only when the immediate child is the
  // real TrendChart.
  const isTrendChart = useMemo(() => {
    if (typeof props.isTrendChart === 'boolean') return props.isTrendChart;
    if (!children) return false;
    return children.type === TrendChart;
  }, [children, props.isTrendChart]);

  // Note creation via in-chart gestures is supported only on the real TrendChart
  // instance. The Trend view double-wraps AnnotatedChart for the same tag (an
  // outer wrap over TagChart, an inner wrap over TrendChart); only the trend
  // instance receives the live zoom/pan domain, so gating on isTrendChart keeps
  // the outer (static-range) overlay from capturing the gesture and saving a note
  // at the wrong timestamp after zoom/pan.
  //
  // create-mode is a page-level toggle, so it is additionally scoped to the
  // focused chart (`activeEntity`, set on pointer-enter) so 'Add note' arms only
  // the chart the user is pointing at, not every trend chart on the page.
  const canCreateNotes = createMode && activeEntity === thisEntityKey
    && isTrendChart && entityType === 'tag' && !!entityId;

  // Focus this chart for note creation while the pointer is over it (only while
  // create-mode is on, to avoid needless dispatches during normal viewing).
  const handlePointerEnter = useCallback(() => {
    if (createMode && thisEntityKey && activeEntity !== thisEntityKey) {
      setActiveAnnotationEntity(thisEntityKey);
    }
  }, [createMode, thisEntityKey, activeEntity, setActiveAnnotationEntity]);

  // Domain the band maps against: prefer the live (zoom-aware) plot domain,
  // falling back to the static time range before the chart has reported it.
  const bandDomain = useMemo(() => {
    if (visibleDomain) return visibleDomain;
    if (timeRange && timeRange.start && timeRange.end) return { start: timeRange.start, end: timeRange.end };
    return null;
  }, [visibleDomain, timeRange]);

  // Nothing to annotate at a scope the API will not serve. Render the chart
  // bare rather than wrapping it in overlay/band/add-note chrome that advertises
  // a capability which can never populate -- the fetch guard above already
  // declines to ask, so the chrome would sit permanently empty and silent.
  if (!isSupportedAnnotatableType(entityType)) {
    return children;
  }

  return (
    <AnnotatedChartContainer className={className} onMouseEnter={handlePointerEnter}>
      {mounted && isTrendChart && annotationsVisible && bandDomain && (
        <RangeLaneBand
          annotations={annotations}
          domain={bandDomain}
          onAnnotationClick={handleAnnotationClick}
          onHoverRange={setHighlightAnnotation}
        />
      )}
      <ChartWrapper>
        {React.cloneElement(children, {
          annotations,
          onAnnotationClick: handleAnnotationClick,
          onVisibleDomainChange: isTrendChart ? handleVisibleDomainChange : undefined,
          highlightAnnotation: isTrendChart ? highlightAnnotation : undefined
        })}
        {mounted && (annotationsVisible || canCreateNotes) && timeRange && (
          <AnnotationOverlay
            annotations={annotations}
            timeRange={timeRange}
            domain={bandDomain}
            loading={annotationsLoading}
            chartHeight={chartHeight}
            chartWidth={chartWidth}
            showControls={showAnnotationControls}
            enableMarkers={typeof overlayMarkers === 'boolean' ? overlayMarkers : !isTrendChart}
            enableDottedLines={typeof overlayDottedLines === 'boolean' ? overlayDottedLines : !isTrendChart}
            onAnnotationClick={handleAnnotationClick}
            onAnnotationCreate={handleAnnotationCreate}
            createMode={canCreateNotes}
          />
        )}
        {pendingNote && (
          <NoteCreationModal
            startTime={pendingNote.startTime}
            endTime={pendingNote.endTime}
            saving={savingNote}
            onSave={handleSaveNote}
            onClose={handleCloseNoteModal}
          />
        )}
      </ChartWrapper>
    </AnnotatedChartContainer>
  );
}

AnnotatedChart.propTypes = {
  // Entity information
  entityType: PropTypes.oneOf(['machine', 'component', 'location', 'tag', 'system']),
  entityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  // Time range for annotations
  timeRange: PropTypes.shape({
    start: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    end: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)])
  }),

  // Optional annotation types filter
  annotationTypes: PropTypes.arrayOf(PropTypes.string),

  // Chart dimensions (passed down from parent chart)
  chartHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  chartWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  // Event handlers
  onAnnotationClick: PropTypes.func,
  onAnnotationCreate: PropTypes.func,

  // UI options
  showAnnotationControls: PropTypes.bool,
  className: PropTypes.string,
  overlayMarkers: PropTypes.bool,
  overlayDottedLines: PropTypes.bool,
  // Optional override; inferred by component identity when unset.
  isTrendChart: PropTypes.bool,

  // Children (the actual chart component)
  children: PropTypes.node.isRequired,

  // Redux state
  annotations: PropTypes.arrayOf(PropTypes.object),
  annotationsVisible: PropTypes.bool,
  createMode: PropTypes.bool,
  activeEntity: PropTypes.string,
  annotationsLoading: PropTypes.bool,
  shouldFetch: PropTypes.func.isRequired,

  // Redux actions
  loadAnnotations: PropTypes.func.isRequired,
  createNote: PropTypes.func.isRequired,
  setActiveAnnotationEntity: PropTypes.func.isRequired
};

AnnotatedChart.defaultProps = {
  entityType: null,
  entityId: null,
  timeRange: null,
  annotationTypes: null,
  chartHeight: '100%',
  chartWidth: '100%',
  onAnnotationClick: null,
  onAnnotationCreate: null,
  showAnnotationControls: true,
  className: '',
  annotations: [],
  annotationsVisible: true,
  createMode: false,
  activeEntity: null,
  annotationsLoading: false,
  // Explicit undefined defaults satisfy react/require-default-props for these
  // optional bools; the overlay falls back based on chart type when unset.
  overlayMarkers: undefined,
  overlayDottedLines: undefined,
  isTrendChart: undefined
};

// Per-instance so each chart gets its own memoized selector and a stable
// `shouldFetch` (a fresh closure each dispatch would defeat connect's memo).
const makeMapStateToProps = () => {
  const getAnnotationsForRange = makeGetAnnotationsForEntityTimeRange();
  let latestState = null;
  const shouldFetch = params => shouldFetchAnnotations(latestState, params);

  return (state, ownProps) => {
    latestState = state;
    const { entityType, entityId } = ownProps;
    return {
      // Read only THIS entity's bucket, so a sibling chart for another tag can't
      // clobber this chart's notes via the shared slice.
      annotations: getAnnotationsForRange(state, entityType, entityId, ownProps.timeRange),
      annotationsVisible: getVisible(state),
      createMode: getCreateMode(state),
      // The chart currently focused for note creation; create-mode arms only it.
      activeEntity: getActiveEntity(state),
      annotationsLoading: getLoadingForEntity(state, entityType, entityId),
      shouldFetch
    };
  };
};

const mapDispatchToProps = {
  loadAnnotations,
  setAnnotationsVisible,
  createNote,
  setActiveAnnotationEntity
};

export default connect(makeMapStateToProps, mapDispatchToProps)(AnnotatedChart);
